import parsePhoneNumber from 'libphonenumber-js';

import { getEmailError } from '@/lib/email-typo';

import quizQuestions from './quiz.json';
import { QuizState } from './quiz-schema';

export type QuizQuestion = (typeof quizQuestions)[number];

export type QuizStepIndex = 0 | 1 | 2;

/**
 * The wizard only changes how the questions are paged. Every step reads and writes the same
 * localforage QuizState, so nothing about the saved payload depends on which step answered what.
 */
export const QUIZ_STEPS = [
  { id: 'pets', label: 'Your pets', event: 'quiz_step_pets' },
  { id: 'questions', label: 'A few questions', event: 'quiz_step_questions' },
  { id: 'details', label: 'Your details', event: 'quiz_step_details' },
] as const;

export const LAST_QUIZ_STEP: QuizStepIndex = 2;

const DETAILS_KEYS = new Set([
  'first_name',
  'last_name',
  'email',
  'phone_number',
  'preferred_contact_time',
  'preferred_contact_time_2',
  'state',
  'referral',
  'terms_of_service',
]);

export function getStepQuestions(step: QuizStepIndex): QuizQuestion[] {
  if (step === 0) return [];

  // Step 2 is every wellbeing question (happy through dissociation, plus gender and eighteen),
  // so a question added to quiz.json lands there by default rather than silently disappearing.
  return quizQuestions.filter((q) => DETAILS_KEYS.has(q.key) === (step === 2));
}

export function getStepForKey(key: string): QuizStepIndex {
  if (key === 'pets') return 0;
  if (key === 'city' || DETAILS_KEYS.has(key)) return 2;

  return quizQuestions.some((q) => q.key === key) ? 1 : 0;
}

export function getDependentQuestion(q: QuizQuestion, quizState: QuizState) {
  const existingAnswer = quizState.answers.find((a) => a.question === q.question)?.text;

  return q.dependentQuestions?.find((d) => d.condition === existingAnswer);
}

export function getIsPhoneNumberValid(phoneNumber?: string) {
  return !!phoneNumber && !!parsePhoneNumber(phoneNumber, 'US')?.isValid();
}

/**
 * A question blocks its step when it is required and empty, when its answer scores negative
 * (the suicide, under 18 and terms of service stops), when a required phone number does not
 * parse, or when an email cannot exist as typed. These are the conditions the form paints red.
 */
export function getIsQuestionInvalid(
  q: { key: string; required: boolean },
  answer: { text: string; points: number } | undefined
) {
  return (
    (!!answer && answer.points < 0) ||
    (q.required && !answer?.text) ||
    (q.key === 'phone_number' && !getIsPhoneNumberValid(answer?.text)) ||
    // Only shape errors, never the "did you mean" guesses, which can be wrong about a real domain.
    (q.key === 'email' && !!getEmailError(answer?.text || ''))
  );
}

export type QuizBlocker = {
  step: QuizStepIndex;
  // Identifies the element to scroll to. Question text, because quiz.json reuses the `sad` key.
  question: string;
  label: string;
};

// Short names for the footer's "still needed" list. The wellbeing questions have no short form,
// so they fall back to their own text, which the list truncates.
const BLOCKER_LABELS: Record<string, string> = {
  pets: 'Add a pet',
  first_name: 'First name',
  last_name: 'Last name',
  email: 'Email',
  phone_number: 'Phone number',
  preferred_contact_time: 'Contact time',
  state: 'State',
  city: 'Metro area',
  terms_of_service: 'Terms of service',
  eighteen: 'Age',
  gender: 'Male or female',
};

/**
 * Everything on a step that stops Continue or Submit, in page order. This is the one definition
 * of a complete step, so the footer list and the gating cannot disagree.
 */
export function getStepBlockers(step: QuizStepIndex, quizState: QuizState): QuizBlocker[] {
  if (step === 0) {
    return quizState.hasPets ? [] : [{ step, question: 'pets', label: BLOCKER_LABELS.pets }];
  }

  return getStepQuestions(step).flatMap((q) => {
    const answer = quizState.answers.find((a) => a.question === q.question);
    const dependentQuestion = getDependentQuestion(q, quizState);
    const dependentAnswer =
      dependentQuestion && quizState.answers.find((a) => a.question === dependentQuestion.question);
    const blockers: QuizBlocker[] = [];

    if (getIsQuestionInvalid(q, answer)) {
      blockers.push({ step, question: q.question, label: BLOCKER_LABELS[q.key] ?? q.question });
    }

    // A dependent question that is on screen counts as required whatever quiz.json says. The
    // metro follow-up is marked optional, but the older isValid arithmetic stops counting its
    // parent once a dependent matches, so skipping it leaves Submit disabled. If this check
    // disagreed, Submit would grey out with nothing on the step to scroll to or highlight.
    if (dependentQuestion && getIsQuestionInvalid({ ...dependentQuestion, required: true }, dependentAnswer)) {
      blockers.push({
        step,
        question: dependentQuestion.question,
        label: BLOCKER_LABELS[dependentQuestion.key] ?? dependentQuestion.question,
      });
    }

    return blockers;
  });
}

export function getIsStepComplete(step: QuizStepIndex, quizState: QuizState) {
  return getStepBlockers(step, quizState).length === 0;
}

/**
 * Reopening the drawer (or refreshing) lands on the first step that still needs something, which
 * is how progress survives without storing the step itself.
 */
export function getFirstIncompleteStep(quizState: QuizState): QuizStepIndex {
  if (!getIsStepComplete(0, quizState)) return 0;
  if (!getIsStepComplete(1, quizState)) return 1;

  return 2;
}
